import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { usePriceFormat } from "hooks/formatting/usePriceFormat";
import { useTranslation } from "react-i18next";
import type { DiscountCodeState } from "slices/transactionSlice";
import { StyledOrderDishItem, StyledOrderDishItemContent } from "./OrderListDishTypeItem";

type OrderListDiscountCodeItemProps = {
	discountCode: DiscountCodeState | null;
	isOrderFinished: boolean;
};

export const OrderListDiscountCodeItem = ({ discountCode, isOrderFinished }: OrderListDiscountCodeItemProps) => {
	const { t } = useTranslation();
	const { formatPrice } = usePriceFormat();

	if (!discountCode) {
		return null;
	}

	const { name, applied, total, unused, currency } = discountCode;
	// The backend does not redeem a code with nothing applied, so nothing is forfeited.
	const isForfeited = isOrderFinished && applied > 0 && unused > 0;

	return (
		<StyledOrderDiscountCodeItem data-testid="discount-code-row">
			<StyledOrderDishItem>
				<StyledBadge>
					<Typography variant="mealMain" component="span">
						%
					</Typography>
				</StyledBadge>
				<StyledOrderDishItemContent>
					<Typography variant="mealMain" color="white" component="p" noWrap={true}>
						{name}
					</Typography>
					<Box display="flex" justifyContent="space-between" alignItems="center">
						<Typography variant="mealSub" color="white">
							{isForfeited
								? t("discountCode.forfeited", { amount: formatPrice(unused, currency) })
								: t("discountCode.used", {
										applied: formatPrice(applied, currency),
										total: formatPrice(total, currency),
									})}
						</Typography>
						<Typography variant="mealMain" color="white">{`−${formatPrice(applied, currency)}`}</Typography>
					</Box>
				</StyledOrderDishItemContent>
			</StyledOrderDishItem>
		</StyledOrderDiscountCodeItem>
	);
};

const StyledOrderDiscountCodeItem = styled(Box)(({ theme }) => ({
	width: `calc(100% + ${theme.spacing(3)})`,
	marginInline: theme.spacing(-1.5),
	padding: `10px ${theme.spacing(1.5)}`,
	marginBottom: theme.spacing(1),
	flexShrink: 0,
	backgroundColor: theme.palette.common.grey[9],
	borderRadius: theme.shape.borderRadius,
}));

const StyledBadge = styled(Box)(({ theme }) => ({
	position: "absolute",
	left: 2,
	top: 12,
	width: 60,
	height: 60,
	borderRadius: "50%",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	backgroundColor: theme.palette.common.white,
	color: theme.palette.copper.main,
}));
